import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listChats, searchHistory } from '../api/adaFetch';
import { useAuth } from '../contexts/Auth';

const MAX_DEPTH = 6;

/**
 * Time-Machine style history: recency-ordered conversations receding into
 * depth, search box cursor-focused on open, query hits across all past
 * message content (server-side indexed recall).
 */
export default function HistoryOverlay({ open, onClose }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Cursor lands in the box the moment it opens.
  useEffect(() => {
    if (open) {
      setQuery('');
      setEntries([]);
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open || !token) return undefined;
    let cancelled = false;
    window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(async () => {
      setLoading(true);
      try {
        if (query.trim()) {
          const data = await searchHistory(query.trim(), token);
          setEntries(Array.isArray(data?.chats) ? data.chats : []);
        } else {
          const data = await listChats(token);
          const arr = Array.isArray(data) ? data : [];
          setEntries(
            arr.map((c) => ({
              id: c.id,
              name: c.name,
              updated_at: c.updated_at || c.time,
              snippet: '',
            }))
          );
        }
      } catch {
        setEntries([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, query.trim() ? 180 : 0);
    return () => {
      cancelled = true;
      window.clearTimeout(debounceRef.current);
    };
  }, [query, open, token]);

  // ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const go = (id) => {
    onClose();
    navigate(`/chat/${id}`);
  };

  return (
    <div
      className="ada-tm"
      role="dialog"
      aria-modal="true"
      aria-label="Conversation history"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ada-tm__panel">
        <div className="ada-tm__search">
          <HistoryRoundedIcon fontSize="small" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all conversations…"
            aria-label="Search conversations"
            spellCheck={false}
          />
          <button
            type="button"
            className="ada-tm__close"
            onClick={onClose}
            aria-label="Close history"
          >
            <CloseRoundedIcon fontSize="small" />
          </button>
        </div>

        <div className="ada-tm__list">
          {loading && !entries.length ? (
            <p className="ada-tm__empty">Searching…</p>
          ) : null}
          {!loading && !entries.length ? (
            <p className="ada-tm__empty">
              {query.trim() ? 'No matches.' : 'No conversations yet.'}
            </p>
          ) : null}
          {entries.slice(0, 40).map((e, i) => {
            const depth = Math.min(i, MAX_DEPTH);
            return (
              <button
                key={e.id}
                type="button"
                className="ada-tm__item"
                style={{
                  '--tm-depth': depth,
                }}
                onClick={() => go(e.id)}
              >
                <span className="ada-tm__name">{e.name}</span>
                <span className="ada-tm__meta">
                  {String(e.updated_at || '').slice(0, 16).replace('T', ' ')}
                </span>
                {e.snippet ? (
                  <span className="ada-tm__snippet">{e.snippet}</span>
                ) : null}
              </button>
            );
          })}
        </div>

        <p className="ada-tm__hint">esc to close · newest first · searches every message</p>
      </div>
    </div>
  );
}
